import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import {
  TrendingUp, Wallet, Percent, AlertTriangle, CalendarClock, Loader2, Trophy, Layers, HandCoins,
} from 'lucide-react';
import { FiltrosPrestamos, InformeButtons } from './_prestamosUtils';

const fmtMoney = (n) =>
  (n || n === 0)
    ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n)
    : '—';
const fmtK = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${Math.round(v / 1e3)}k`;
  return `$${Math.round(v)}`;
};

const Kpi = ({ icon: Icon, label, value, sub, tone = 'slate', testid }) => {
  const tones = {
    slate: 'text-slate-800 dark:text-slate-100', emerald: 'text-emerald-600',
    rose: 'text-rose-600', teal: 'text-teal-600', amber: 'text-amber-600',
  };
  return (
    <Card className="p-4 border-slate-200 dark:border-slate-800" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1 text-slate-500">
        <Icon className="h-4 w-4" /><span className="text-xs font-medium">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
};

const Rentabilidad = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ desde: '', hasta: '', frecuencia: '' });

  const load = async (f) => {
    setLoading(true);
    try {
      const r = await api.get('/motor/rentabilidad', { params: f || filtros });
      setData(r.data);
    } catch (e) {
      toast.error('No se pudo cargar la rentabilidad');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(filtros); }, []); // eslint-disable-line

  const u = data?.universo || {}, m = data?.mora || {}, p = data?.proyeccion || { monto_futuro: {}, ganancia_proyectada: {} };
  const kr = data?.kpis_rango || {};
  const rango = data?.rango || {};
  const serie = data?.serie_mensual || [];
  const maxSerie = Math.max(1, ...serie.map((s) => s.ingreso));
  const frec = Object.entries(data?.por_frecuencia || {});

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-6xl mx-auto" data-testid="rentabilidad">
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><TrendingUp className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Rentabilidad</h1>
            <p className="text-sm text-slate-500">Ganancia, mora y proyección de cobro de la cartera ({u.prestamos || 0} préstamos)</p>
          </div>
        </div>
        <InformeButtons tipo="rentabilidad" params={filtros} />
      </div>

      <FiltrosPrestamos value={filtros} onChange={setFiltros} onApply={(f) => load(f)} dateFields={[]} showEstado={false} showFrecuencia />

      <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
        <CalendarClock className="h-4 w-4" /> Período {rango.desde || '—'} → {rango.hasta || '—'}{rango.frecuencia ? ` · ${rango.frecuencia}` : ''}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="rent-rango">
        <Kpi icon={HandCoins} label="Ingreso realizado (período)" value={fmtK(kr.ingreso_realizado)} tone="emerald" sub={fmtMoney(kr.ingreso_realizado)} testid="rent-rango-realizado" />
        <Kpi icon={TrendingUp} label="Ingreso esperado (período)" value={fmtK(kr.ingreso_esperado)} sub={fmtMoney(kr.ingreso_esperado)} />
        <Kpi icon={Percent} label="% Recupero (período)" value={`${kr.pct_recupero ?? 0}%`} tone="teal" />
      </div>

      {loading && <div className="py-4 text-center text-slate-400" data-testid="rent-loading"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
      {!loading && !data && <div className="py-8 text-center text-slate-400">Sin datos.</div>}

      {/* Universo */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Kpi icon={Wallet} label="Capital colocado" value={fmtK(u.capital_colocado)} sub={fmtMoney(u.capital_colocado)} testid="rent-capital" />
        <Kpi icon={TrendingUp} label="Ganancia esperada" value={fmtK(u.ganancia_esperada)} sub={fmtMoney(u.ganancia_esperada)} />
        <Kpi icon={TrendingUp} label="Ganancia realizada" value={fmtK(u.ganancia_realizada)} tone="emerald" sub={fmtMoney(u.ganancia_realizada)} testid="rent-realizada" />
        <Kpi icon={Percent} label="% Recupero" value={`${u.pct_recupero}%`} tone="teal" />
        <Kpi icon={AlertTriangle} label="Mora total" value={fmtK(m.total_mora)} tone="rose" sub={`${m.prestamos_con_mora} préstamos · ${m.tasa_mora}%`} testid="rent-mora" />
      </div>

      {/* Recupero bar */}
      <Card className="p-4 mb-6 border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-slate-500">Recupero de la ganancia esperada</span>
          <span className="font-semibold text-teal-600">{u.pct_recupero}%</span>
        </div>
        <div className="h-3 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
          <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${Math.min(100, u.pct_recupero)}%` }} />
        </div>
      </Card>

      {/* Proyección 30/60/90 */}
      <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Proyección de cobro (acumulada)</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="rent-proyeccion">
        {['30', '60', '90'].map((k) => (
          <Card key={k} className="p-4 border-slate-200 dark:border-slate-800">
            <p className="text-xs uppercase tracking-wider text-slate-400">Próximos {k} días</p>
            <p className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-1">{fmtMoney(p.monto_futuro[k])}</p>
            <p className="text-xs text-slate-500">por vencer</p>
            <p className="text-sm font-medium text-emerald-600 mt-2">+ {fmtMoney(p.ganancia_proyectada[k])}</p>
            <p className="text-xs text-slate-400">ganancia proyectada</p>
          </Card>
        ))}
      </div>

      {/* Serie mensual */}
      <Card className="p-5 mb-6 border-slate-200 dark:border-slate-800">
        <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-4">Ingreso realizado — últimos 12 meses</h2>
        <div className="flex items-end gap-2 h-40" data-testid="rent-serie">
          {serie.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end group">
              <span className="text-[9px] text-slate-500 mb-1 opacity-0 group-hover:opacity-100 whitespace-nowrap">{fmtK(s.ingreso)}</span>
              <div className="w-full bg-teal-500/80 hover:bg-teal-500 rounded-t transition-all" style={{ height: `${Math.max(2, (s.ingreso / maxSerie) * 100)}%` }} title={`${s.mes}: ${fmtMoney(s.ingreso)}`} />
              <span className="text-[9px] text-slate-400 mt-1">{s.mes.slice(2)}</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top 10 */}
        <Card className="lg:col-span-2 p-5 border-slate-200 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2"><Trophy className="h-4 w-4" /> Top 10 por ganancia realizada</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rent-top10">
              <thead className="text-slate-400 text-xs">
                <tr>
                  <th className="text-left py-1.5 font-medium">Cliente</th>
                  <th className="text-right py-1.5 font-medium">Monto</th>
                  <th className="text-right py-1.5 font-medium">Realizada</th>
                  <th className="text-right py-1.5 font-medium">%</th>
                  <th className="text-right py-1.5 font-medium">Mora</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top10 || []).map((t, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 text-slate-700 dark:text-slate-200">
                      <span className="block truncate max-w-[180px]">{t.nombre || t.cuit}</span>
                      <span className="text-[10px] text-slate-400">{t.frecuencia}</span>
                    </td>
                    <td className="py-2 text-right text-slate-600 dark:text-slate-300">{fmtK(t.monto_solicitado)}</td>
                    <td className="py-2 text-right text-emerald-600 font-medium">{fmtK(t.ingreso_realizado)}</td>
                    <td className="py-2 text-right text-slate-500">{t.pct_realizado}%</td>
                    <td className={`py-2 text-right ${t.total_mora > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{t.total_mora > 0 ? fmtK(t.total_mora) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Por frecuencia */}
        <Card className="p-5 border-slate-200 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3 flex items-center gap-2"><Layers className="h-4 w-4" /> Por frecuencia</h2>
          <div className="space-y-3">
            {frec.map(([name, v]) => (
              <div key={name} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700 dark:text-slate-200">{name}</span>
                  <span className="text-xs text-slate-400">{v.count} préstamos</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span className="text-slate-400">Realizada</span><p className="font-medium text-emerald-600">{fmtK(v.ingreso_realizado)}</p></div>
                  <div><span className="text-slate-400">Mora</span><p className="font-medium text-rose-600">{fmtK(v.total_mora)} · {v.tasa_mora}%</p></div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Rentabilidad;
